import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild, OnInit } from '@angular/core';
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { cloneDeep } from 'lodash-es';
import { first } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatTooltip } from '@angular/material/tooltip';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { UserStore } from '../../../core/services/user.store';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FormBuilderDropListRegistry } from './form-builder-drop-list-registry.service';

// Lists holding plain field definitions are interchangeable drop targets; container lists
// (wizardFields = steps, tabsFields, pagesFields) only accept items of their own kind.
const FIELD_LIST_KEYS = ['fields', 'groupFields', 'accordionFields', 'repeatFields'];
const listKind = (key: unknown): string => (FIELD_LIST_KEYS.includes(key as string) ? 'field' : String(key));

@Component({
  selector: 'formly-repeat-section-accordion',
  template: `
    <div class="my-4" [attr.data-cy]="field.props?.keyPath ?? null">
      @if (props.label && !props.hideLabel) {
        @if (props.labelType === undefined || props.labelType === 'titel') {
          <h3 class="font-bold mb-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </h3>
        } @else {
          <label class="block text-sm font-medium text-default mb-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </label>
        }
      }
      @if (props.description) {
        <p class="mb-2">{{ props.description }}</p>
      }

      <div
        cdkDropList
        [cdkDropListData]="this"
        [cdkDropListConnectedTo]="connectedDropLists()"
        [cdkDropListEnterPredicate]="enterPredicate"
        (cdkDropListDropped)="drop($event)"
        class="inline-block w-full">
        @if (field?.fieldGroup?.length >= 1) {
        <mat-accordion class=" inline-block w-full">
          @for (item of field.fieldGroup; track trackByFn($index, item); let index = $index) {
            <mat-expansion-panel
              cdkDrag="false"
              [cdkDragData]="model?.[index]"
              [expanded]="expandedIndex() === index"
              (opened)="expandedIndex.set(index)"
              (closed)="onPanelClosed(index)"
              class="repeater-item border mb-4">
              <!-- Drag Placeholder -->
              <div *cdkDragPlaceholder class="custom-placeholder"></div>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon
                    class="text-light cursor-move"
                    cdkDragHandle
                    svgIcon="mat_outline:drag_indicator"></mat-icon>
                  <div class="mx-2 flex-0" [class.opacity-60]="!model?.[index]?.label">
                    {{
                      model?.[index]?.label
                        ? model?.[index]?.label
                        : model?.[index]?.props?.label
                          ? model?.[index]?.props?.label
                          : ''
                    }}
                  </div>
                  @if (model?.[index]?.typeName) {
                    <div class="opacity-60 flex-1 font-normal truncate text-sm">
                      @if (model?.[index]['type'] !== 'pages') {
                        <span>&nbsp;-&nbsp;</span>
                      }
                      {{ model?.[index]['typeName'] }}
                    </div>
                  }
                </mat-panel-title>
                @if (!form.disabled && !item.model?.lock && !field.props.disabled) {
                  <button
                    type="button"
                    class="mr-2"
                    mat-icon-button
                    matTooltip="Dupliceren"
                    (click)="duplicate(index + 1, model?.[index])">
                    <mat-icon class="icon-size-4">content_copy</mat-icon>
                  </button>
                }
                @if (
                  !form.disabled &&
                  !item.model?.lock &&
                  !field.props.disabled &&
                  props.initialCount < field.fieldGroup.length &&
                  !hideAddButton()
                ) {
                  <button type="button" class="mr-4" mat-icon-button matTooltip="Verwijderen" (click)="remove(index)">
                    <mat-icon class="icon-size-4">close</mat-icon>
                  </button>
                }
              </mat-expansion-panel-header>
              <ng-template matExpansionPanelContent>
                @defer {
                  <formly-field class="flex-1" [field]="item"></formly-field>
                } @placeholder {
                  <div class="text-center w-full">
                    <mat-spinner class="m-auto mt-4 mb-8"></mat-spinner>
                  </div>
                }
              </ng-template>
            </mat-expansion-panel>
          }
          <!--        <div class="custom-placeholder" *cdkDragPlaceholder></div>-->
        </mat-accordion>
        } @else if (crossLevel) {
          <div class="rounded-lg border border-dashed p-4 text-center text-sm text-accent">
            Sleep hier een {{ (props.repeatLabel ?? 'item').toLowerCase() }} naartoe
          </div>
        }
      </div>
      <div class="py-4">
        @if (!form.disabled && !field.props.disabled && !hideAddButton()) {
          <button
            type="button"
            class="small-button dd-action-button"
            [attr.aria-label]="props.addText || 'Toevoegen'"
            mat-flat-button
            (click)="add(field.fieldGroup?.length, getDefaultModelForIndex(field.fieldGroup?.length))">
            <mat-icon>add</mat-icon>
            {{ field.props.addText ? field.props.addText : field.props.label + ' toevoegen ' }}
          </button>
        }
      </div>
    </div>
  `,
  imports: [
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    CdkDragHandle,
    MatTooltip,
    InfoTooltipComponent,
    MatProgressSpinner,
  ],
  styles: `
    .custom-placeholder {
      background: #ccc;
      border: dotted 3px #999;
      min-height: 60px;
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyRepeatAccordionTypeComponent extends FieldArrayType implements OnInit {
  static readonly NESTED_FIELD_KEYS = [
    'groupFields',
    'accordionFields',
    'repeatFields',
    'tabsFields',
    'pagesFields',
    'wizardFields',
  ] as const;

  userStore = inject(UserStore);
  formService = inject(FormService);
  readonly #fuseConfirmationService = inject(FuseConfirmationService);
  // Only the form builder's formly-form carries cdkDropListGroup; runtime repeaters resolve
  // null here and keep their isolated per-list drag behavior.
  readonly #builderGroup = inject(CdkDropListGroup, { optional: true }) as CdkDropListGroup<CdkDropList> | null;
  readonly #dropListRegistry = inject(FormBuilderDropListRegistry);
  // Not #-private: Angular query functions don't support ES-private class members (NG1053).
  private readonly dropList = viewChild(CdkDropList);
  readonly crossLevel = this.#builderGroup !== null;
  // Deepest-first order so the CDK's first-match hit-test resolves nested lists before the
  // outer lists that also contain the pointer (see FormBuilderDropListRegistry).
  readonly connectedDropLists = computed(() =>
    this.#builderGroup ? this.#dropListRegistry.connectedLists(this.#builderGroup) : []
  );
  hideAddButton = signal(false);
  // One panel open at a time: the last item on load, or the most recently
  // added/duplicated one. Content renders lazily via @defer, so only the open
  // panel's subtree is built — rendering every item up front is the dominant cost.
  expandedIndex = signal<number | null>(null);

  /**
   * Cross-level acceptance: only drags from another repeat-accordion of the same list kind
   * (field lists interchange; steps/tabs/pages only with their own kind), and never into a
   * list nested inside the dragged item itself — that transfer would orphan the subtree.
   */
  readonly enterPredicate = (drag: CdkDrag): boolean => {
    const source = drag.dropContainer?.data;
    if (!(source instanceof FormlyRepeatAccordionTypeComponent)) {
      return false;
    }
    if (listKind(source.field?.key) !== listKind(this.field?.key)) {
      return false;
    }
    for (let field = this.field as FormlyFieldConfig | undefined; field; field = field.parent) {
      if (field.model === drag.data) {
        return false;
      }
    }
    return true;
  };

  drop(event: CdkDragDrop<FormlyRepeatAccordionTypeComponent>) {
    if (event.previousContainer !== event.container) {
      this.#dropFromOtherList(event);
      return;
    }
    const modelCopy = this.model;
    moveItemInArray(modelCopy, event.previousIndex, event.currentIndex);
    this.field.formControl.patchValue(modelCopy);
    // Indices shift after a reorder; collapse instead of tracking the moved panel.
    this.expandedIndex.set(null);
    this.options.build(this.field);
  }

  #dropFromOtherList(event: CdkDragDrop<FormlyRepeatAccordionTypeComponent>) {
    const source = event.previousContainer.data;
    if (!(source instanceof FormlyRepeatAccordionTypeComponent)) {
      return;
    }
    const itemModel = source.model?.[event.previousIndex];
    if (itemModel == null) {
      return;
    }

    // Moving a saved field to another level changes its storage keyPath (fields nest under
    // their step/tab/group key at runtime); existing data stays behind at the old path.
    if (!this.#containsOriginalField(itemModel)) {
      this.#transferFrom(source, event.previousIndex, event.currentIndex, itemModel);
      return;
    }
    this.#fuseConfirmationService
      .open({
        title: 'Veld verplaatsen',
        message:
          `Het veld "${itemModel.label ?? itemModel.key ?? ''}" is al in gebruik in de database. ` +
          'Verplaatsen wijzigt waar de waarde wordt opgeslagen; bestaande gegevens blijven op de oude locatie staan.',
        icon: {
          show: true,
          name: 'heroicons_outline:exclamation-triangle',
          color: 'warn',
        },
        actions: {
          cancel: { label: 'Annuleren' },
          confirm: { label: 'Verplaatsen', color: 'warn' },
        },
      })
      .afterClosed()
      .pipe(first())
      .subscribe(result => {
        // On cancel nothing was mutated, so the dragged panel simply stays in its source list.
        if (result === 'confirmed') {
          this.#transferFrom(source, event.previousIndex, event.currentIndex, itemModel);
        }
      });
  }

  #transferFrom(
    source: FormlyRepeatAccordionTypeComponent,
    previousIndex: number,
    currentIndex: number,
    itemModel: any
  ) {
    // Re-run per-field init hooks on the controls rebuilt at the new location (same reason
    // duplicate() clears these). The id is kept, so reference checks and the form builder's
    // deletion detection keep treating it as the same field.
    delete itemModel._initialized;
    delete itemModel._originalType;
    // Target first: adding can rebuild an ancestor subtree that recreates the source
    // component; remove() on a stale instance still works because it mutates the shared
    // field config and model, not component state.
    this.add(currentIndex, itemModel);
    source.remove(previousIndex);
  }

  #containsOriginalField(itemModel: any): boolean {
    if (!itemModel || typeof itemModel !== 'object') {
      return false;
    }
    if (itemModel.isOriginalField === true) {
      return true;
    }
    return FormlyRepeatAccordionTypeComponent.NESTED_FIELD_KEYS.some(
      key => Array.isArray(itemModel[key]) && itemModel[key].some((child: any) => this.#containsOriginalField(child))
    );
  }

  constructor() {
    super();
    // Register this list (with its DOM depth) for the builder-wide deepest-first ordering;
    // the effect re-runs if the drop list is ever recreated and cleans up on destroy.
    effect(onCleanup => {
      const dropList = this.dropList();
      const group = this.#builderGroup;
      if (!group || !dropList) {
        return;
      }
      this.#dropListRegistry.register(group, dropList);
      onCleanup(() => this.#dropListRegistry.unregister(group, dropList));
    });
  }

  trackByFn(index: number, item: any): any {
    return item.id;
  }

  override add(i?: number, initialModel?: any, options?: { markAsDirty: boolean }) {
    super.add(i, initialModel, options);
    this.expandedIndex.set(i ?? (this.field.fieldGroup?.length ?? 1) - 1);
  }

  override remove(i: number, options?: { markAsDirty: boolean }) {
    super.remove(i, options);
    const expanded = this.expandedIndex();
    if (expanded === i) {
      this.expandedIndex.set(null);
    } else if (expanded !== null && expanded > i) {
      this.expandedIndex.set(expanded - 1);
    }
  }

  onPanelClosed(index: number) {
    if (this.expandedIndex() === index) {
      this.expandedIndex.set(null);
    }
  }

  ngOnInit() {
    if (this.props.initialCount === undefined) {
      this.props.initialCount = 0;
    }
    if (this.props.hideAddButtonForRoles != null && Object.keys(this.props.hideAddButtonForRoles).length > 0) {
      this.hideAddButton.set(this.field.props.hideAddButtonForRoles[this.userStore.currentUserId()]);
    }
    this.validateLength();

    if (this.expandedIndex() === null && this.field.fieldGroup?.length) {
      this.expandedIndex.set(this.field.fieldGroup.length - 1);
    }
  }

  /**
   * Get the default model for a specific index, delegating to FormService.
   */
  getDefaultModelForIndex(index: number): any {
    return this.formService.getDefaultModelForIndex(this.field, index);
  }

  /**
   * Apply defaultCount only for NEW entries (when there's no existing data).
   * If there's already saved data, we just load that - defaultCount is not enforced.
   */
  private validateLength() {
    // Only apply defaultCount for new entries — not for edits.
    if (!this.options?.formState?.isNew || this.model?.length > 0) {
      return;
    }

    const targetCount = this.props.defaultCount ?? this.props.initialCount;
    // Capture starting length BEFORE the loop - fieldGroup.length changes after each add()
    const startingLength = this.field.fieldGroup?.length ?? 0;

    if (startingLength < targetCount) {
      const itemsToAdd = targetCount - startingLength;
      for (let i = 0; i < itemsToAdd; i++) {
        const currentIndex = startingLength + i;
        this.add(currentIndex, this.getDefaultModelForIndex(currentIndex));
      }
    }
  }

  duplicate(index: number, item) {
    const clonedItem = cloneDeep(item);
    this.regenerateIds(clonedItem);
    this.add(index, clonedItem);
    const modelCopy = this.model;
    this.field.formControl.patchValue(modelCopy);
    this.options.build(this.field);
  }

  /**
   * Recursively regenerate IDs on a duplicated field model to prevent DOM ID collisions.
   */
  private regenerateIds(item: any): void {
    if (!item || typeof item !== 'object') return;

    if (item.id) {
      item.id = crypto.randomUUID();
    }

    // Cloned models must re-run per-field init hooks (type-change dialog wiring,
    // typeName sync); the copied _initialized flag would silently skip them.
    delete item._initialized;
    delete item._originalType;

    for (const childKey of FormlyRepeatAccordionTypeComponent.NESTED_FIELD_KEYS) {
      if (Array.isArray(item[childKey])) {
        item[childKey].forEach((child: any) => this.regenerateIds(child));
      }
    }
  }
}
